import { makeStyles, withStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { path } from 'ramda'
import React, { FC } from 'react'
import HelpContainer from 'core/components/HelpContainer'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

const useStyles = makeStyles((theme: Theme) => ({
  rowHelp: {
    width: 24,
  },
  rowHeader: {
    display: 'flex',
    flexShrink: 0,
    width: 200,
  },
  rowValue: {
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-all',
  },
}))

// INFOPANEL-FR-001: One "label: value" table row, with a help icon only when helpMessage is set
// String values render in a span, anything else in a div so block content stays valid markup
export const DetailRow: FC<{
  label: string
  value: string | React.ReactNode
  helpMessage?: string
}> = ({ label, value, helpMessage }) => {
  const { rowHeader, rowValue, rowHelp } = useStyles({})
  return (
    <tr>
      <td>
        <Text className={rowHeader} variant="body2" component="span">
          {label}:
        </Text>
      </td>
      <td>
        <Text
          className={rowValue}
          variant="caption1"
          component={typeof value === 'string' ? 'span' : 'div'}
        >
          {value}
        </Text>
      </td>
      <td className={rowHelp}>
        {!!helpMessage && <HelpContainer title={helpMessage} color="black" />}
      </td>
    </tr>
  )
}

export interface IDetailFields<T> {
  id: string
  title: string
  required?: boolean
  helpMessage?: string | React.ReactNode
  condition?: (cluster: T) => boolean
  render?: (value: any, item: any) => string | React.ReactNode
  renderExtraContent?: (value: any, item: any) => React.ReactNode
}

interface FieldValue {
  value?: string | React.ReactNode
  helpMessage?: string | React.ReactNode
  extraContent?: React.ReactNode
}
export interface FieldsForCardsProps {
  [title: string]: FieldValue
}

/**
 * Gets fields for the InfoPanel component
 *
 * Ex. getFieldsForCard(fields, cluster)
 */
// INFOPANEL-FR-002, INFOPANEL-FR-003: Picks which fields to show (value present, required, or
// condition) and builds each one's value, helpMessage and extraContent, keyed by field title
export function getFieldsForCard<T>(fields: Array<IDetailFields<T>>, item: T): FieldsForCardsProps {
  const fieldsToDisplay = {}
  fields.forEach((field) => {
    const {
      id,
      title,
      required = false,
      condition,
      render,
      helpMessage,
      renderExtraContent,
    } = field
    const value = path<string | boolean>(id.split('.'), item)
    const hasValue = !!value || value === false
    const shouldRender = condition ? condition(item) : required || hasValue
    if (shouldRender && (required || hasValue)) {
      fieldsToDisplay[title] = {
        value: render ? render(value, item) : value,
        helpMessage,
        extraContent: renderExtraContent ? renderExtraContent(value, item) : null,
      }
    }
  })
  return fieldsToDisplay
}

const styles = (theme) => ({
  root: {},
  row: {
    width: '100%',
  },
  half: {
    display: 'inline-block',
    width: '50%',
  },
  cardContent: {
    width: '100%',
    margin: 0,
    padding: theme.spacing(2, 3, 3),
    borderSpacing: 0,
    '& tr:not(:last-child) td': {
      paddingBottom: theme.spacing(1.5),
    },
  },
  // Freeform body: no imposed padding/spacing — callers lay out and space their own content,
  // since freeform bodies (e.g. a list of callout rows) don't share one fixed padding shape.
  cardBody: {
    width: '100%',
  },
  card: {
    width: '100%',
    border: `solid 1px ${theme.components.card.border}`,
    borderRadius: 4,
    background: theme.components.card.background,
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.5),
    color: theme.components.card.text,
    background: theme.palette.grey['50'],
    padding: theme.spacing(1.5, 2),
    borderBottom: `solid 1px ${theme.components.card.border}`,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
})

interface DetailRowProps {
  classes?: any
  items: any[]
}

// @ts-ignore
const DetailRowDiv = withStyles(styles)(({ classes, items }: DetailRowProps) => {
  return Object.entries(items).map(([name, { value, helpMessage }]) => (
    <DetailRow key={name} label={name} value={value} helpMessage={helpMessage} />
  ))
})

// INFOPANEL-FR-005: Renders one DetailRow per entry of an items object (title -> { value, helpMessage })
// @ts-ignore
const renderDetailRow = (items) => <DetailRowDiv items={items} />

// See InfoPanel.spec.md for INFOPANEL-FR-004 (header) and INFOPANEL-FR-005 (table or freeform body)
const InfoPanel = withStyles(styles)(
  ({
    classes,
    items = [],
    children = undefined,
    className = undefined,
    title,
    subtitle = undefined,
    dataTestId,
  }: InfoPanelProps) => (
    <div className={clsx(classes.card, className)}>
      <div className={classes.header}>
        <Text variant="subtitle2" component="h3" data-testid={`info-panel--title-${dataTestId}`}>
          {title}
        </Text>
        {subtitle && (
          <Text variant="body2" component="p" data-testid={`info-panel--subtitle-${dataTestId}`}>
            {subtitle}
          </Text>
        )}
      </div>
      {children && (
        <div className={classes.cardBody} data-testid={`info-panel--custom-body-${dataTestId}`}>
          {children}
        </div>
      )}
      {!children && (
        <table className={classes.cardContent} data-testid={`info-panel--table-${dataTestId}`}>
          <tbody data-testid={`info-panel--tbody-${dataTestId}`}>
            {Array.isArray(items) ? items.map(renderDetailRow) : renderDetailRow(items)}
          </tbody>
        </table>
      )}
    </div>
  ),
)

interface InfoPanelProps {
  title: string
  subtitle?: string | React.ReactNode
  classes?: any
  items?: any
  children?: React.ReactNode
  className?: string
  dataTestId: string
}

export default InfoPanel
